import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Plus, Star, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type { OrganizationVertical, Uuid, VerticalDepartment } from "@tilt/admin-sdk";

/**
 * V0022 - manages an organization's enabled verticals, its default
 * among them, and the curated department subset per vertical.
 *
 * Drop-in tab renderer: pass the organization id, component handles
 * its own queries + mutations + validation.
 */
export function VerticalsTab(props: { organizationId: Uuid }): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ["org-verticals", props.organizationId],
    queryFn: () => apiClient.listOrganizationVerticals(props.organizationId),
  });
  const catalogQuery = useQuery({
    queryKey: ["verticals"],
    queryFn: () => apiClient.listVerticals(),
    staleTime: 60_000,
  });
  const catalog = catalogQuery.data?.verticals ?? [];
  const enabled = useMemo(() => list.data?.verticals ?? [], [list.data]);
  const enabledIds = useMemo(() => new Set(enabled.map((v) => v.vertical_id)), [enabled]);
  const remaining = catalog.filter((v) => !enabledIds.has(v.id));

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["org-verticals", props.organizationId] });
  };

  const [adding, setAdding] = useState(false);
  const [pendingVid, setPendingVid] = useState("");
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      apiClient.addOrganizationVertical(props.organizationId, {
        vertical_id: pendingVid,
        is_default: enabled.length === 0,
      }),
    onSuccess: () => {
      setAdding(false);
      setPendingVid("");
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4 shadow-sm sm:p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">Verticals enabled for this organization</h2>
            <p className="text-xs text-muted-foreground">
              Each location under this org picks one of the verticals below. Exactly one is the
              default; child locations inherit it unless they override.
            </p>
          </div>
          {remaining.length > 0 && !adding && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <Plus className="h-3.5 w-3.5" /> Add vertical
            </button>
          )}
        </div>

        {adding && (
          <div className="mb-3 rounded-md border bg-muted/30 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={pendingVid}
                onChange={(e) => setPendingVid(e.target.value)}
                className="rounded-md border bg-background px-3 py-1.5 text-sm"
              >
                <option value="">Pick a vertical…</option>
                {remaining.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.display_name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => add.mutate()}
                disabled={!pendingVid || add.isPending}
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {add.isPending ? "Adding…" : "Add"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAdding(false);
                  setPendingVid("");
                  setError(null);
                }}
                className="rounded-md border px-3 py-1.5 text-xs"
              >
                Cancel
              </button>
            </div>
            {error && <p className="mt-2 text-[11px] text-destructive">{error}</p>}
          </div>
        )}

        {list.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {enabled.length === 0 && !list.isLoading && (
          <p className="rounded-md border border-dashed bg-muted/20 p-4 text-center text-xs text-muted-foreground">
            No verticals enabled yet. Add one above to let locations pick it.
          </p>
        )}
        <ul className="space-y-3">
          {enabled.map((ov) => (
            <VerticalRow
              key={ov.id}
              organizationId={props.organizationId}
              ov={ov}
              catalog={catalog}
              onMutated={invalidate}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function VerticalRow(props: {
  organizationId: Uuid;
  ov: OrganizationVertical;
  catalog: { id: Uuid; slug: string; display_name: string; departments: VerticalDepartment[] }[];
  onMutated: () => void;
}): JSX.Element {
  const { ov, catalog, onMutated } = props;
  const [error, setError] = useState<string | null>(null);
  const [deptOpen, setDeptOpen] = useState(false);

  const setDefault = useMutation({
    mutationFn: () =>
      apiClient.setOrganizationVerticalDefault(props.organizationId, ov.vertical_id, true),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const remove = useMutation({
    mutationFn: () => apiClient.removeOrganizationVertical(props.organizationId, ov.vertical_id),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const catalogEntry = catalog.find((c) => c.id === ov.vertical_id);
  const catalogDepts = catalogEntry?.departments ?? [];
  const currentSubsetIds = new Set(ov.departments.map((d) => d.id));
  const [pendingSubsetIds, setPendingSubsetIds] = useState<Set<string>>(
    new Set(ov.departments.map((d) => d.id)),
  );

  const saveSubset = useMutation({
    mutationFn: () =>
      apiClient.putOrganizationVerticalDepartments(
        props.organizationId,
        ov.vertical_id,
        Array.from(pendingSubsetIds),
      ),
    onSuccess: () => {
      setError(null);
      setDeptOpen(false);
      onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const dirty = useMemo(() => {
    if (pendingSubsetIds.size !== currentSubsetIds.size) return true;
    for (const id of pendingSubsetIds) if (!currentSubsetIds.has(id)) return true;
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSubsetIds, ov.departments]);

  const effectiveDeptLabel = ov.departments.length
    ? `${ov.departments.length} curated`
    : `All ${catalogDepts.length} from catalog`;

  return (
    <li className="rounded-lg border bg-background p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-medium">{ov.vertical_display_name}</span>
          {ov.is_default && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
              <Star className="h-3 w-3" /> Default
            </span>
          )}
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
            {effectiveDeptLabel}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {!ov.is_default && (
            <button
              type="button"
              onClick={() => setDefault.mutate()}
              disabled={setDefault.isPending}
              className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
            >
              Set default
            </button>
          )}
          <button
            type="button"
            onClick={() => setDeptOpen((v) => !v)}
            className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
          >
            Departments
          </button>
          <button
            type="button"
            onClick={() => {
              if (!window.confirm(`Remove "${ov.vertical_display_name}" from this organization?`))
                return;
              remove.mutate();
            }}
            disabled={remove.isPending || ov.is_default}
            title={ov.is_default ? "Pick a different default first" : "Remove"}
            className="rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {deptOpen && (
        <div className="mt-3 rounded-md bg-muted/20 p-3">
          <p className="mb-2 text-[11px] text-muted-foreground">
            Curate which departments this org uses for this vertical. Empty subset = inherit the
            full vertical catalog ({catalogDepts.length} departments).
          </p>
          {catalogDepts.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              This vertical has no departments in the catalog yet.
            </p>
          ) : (
            <ul className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
              {catalogDepts.map((d) => {
                const on = pendingSubsetIds.has(d.id);
                return (
                  <li key={d.id}>
                    <label
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1 text-xs",
                        on ? "border-primary/40 bg-primary/5" : "bg-background",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) => {
                          const next = new Set(pendingSubsetIds);
                          if (e.target.checked) next.add(d.id);
                          else next.delete(d.id);
                          setPendingSubsetIds(next);
                        }}
                        className="h-3.5 w-3.5 accent-primary"
                      />
                      <span>{d.display_name}</span>
                      {on && <Check className="ml-auto h-3 w-3 text-primary" aria-hidden="true" />}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => saveSubset.mutate()}
              disabled={!dirty || saveSubset.isPending}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saveSubset.isPending ? "Saving…" : "Save subset"}
            </button>
            <button
              type="button"
              onClick={() => {
                setPendingSubsetIds(new Set(ov.departments.map((d) => d.id)));
                setDeptOpen(false);
              }}
              className="rounded-md border px-3 py-1.5 text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-2 rounded-md bg-destructive/10 p-2 text-[11px] text-destructive">
          {error}
        </div>
      )}
    </li>
  );
}
